/**
 * The plan's panel (§8.1–§8.3, Board 11, Board 12): **How do you bill
 * this?**, the five kind cards, a rule, the chosen kind's rows, and the Reads
 * band. On a phone (Board 16 cell 2) the cards are a column and the chosen one
 * opens its rows in place.
 *
 * Every number shown here is the server's: the deposit's dollars are its
 * invoice, a stage's are its row of the plan view. The browser only sends
 * what was typed (rule 3).
 */
import { useState, type ReactNode } from 'react'
import {
  BILLING_KIND_CARDS, BILLING_KIND_QUESTION, BILLING_KIND_QUESTION_SUB, BILLING_PLAN_KINDS,
  BILLING_ROWS, DEFAULT_BILLING_DAY, forWindow, planProposalLine, planReads, prorateHint, usd,
  type BillingPlanKind, type BillingWordsContext, type CompletionMode, type PartPeriod,
  type PeriodBasis, type PeriodUnit, type QuoteBillingPlanView, type RestMode,
  type TimeMaterialsTrigger,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { MenuSelect } from '../../components/menu-select.js'
import { Segmented } from '../../components/primitives.js'
import { DateInput } from '../../components/form/index.js'
import { QtyInput } from '../../builder/rows/line-row.js'
import { IconCheck } from '../../frame/icons.js'
import { DayBox, KindGlyph, LinkButton, ReadsBand, Row, Said, UnitToggle } from './parts.js'
import { Stages } from './stages.js'

export type BillingLayout = 'desktop' | 'portrait' | 'phone'

type Patch = (body: Record<string, unknown>) => Promise<unknown>

/** A write as a number box wants it: false puts the box back to what the server holds. */
const settled = (write: Promise<unknown>): Promise<boolean> => write.then(() => true, () => false)

export interface KindPanelProps {
  plan: QuoteBillingPlanView
  ctx: BillingWordsContext
  visits: readonly { id: string; name: string }[]
  layout: BillingLayout
  busy: boolean
  onPatch: Patch
  /**
   * A package's plan (visits §10.2): no dates yet, so no *Change months* and
   * no stage *On a date* — the two removals the contract names.
   */
  dateless?: boolean
}

export function KindPanel(props: KindPanelProps) {
  const { plan, ctx, layout, busy, onPatch } = props
  const phone = layout === 'phone'
  const pick = (kind: BillingPlanKind) => { if (kind !== plan.kind) void onPatch({ kind }) }

  const rows = (
    <div className="flex flex-col gap-[11px]">
      <KindRows {...props} />
      <ReadsBand reads={planReads(plan, ctx)} line={planProposalLine(plan, ctx)} />
    </div>
  )

  if (phone) {
    return (
      <div className="flex flex-col gap-2.5">
        <b className="text-[14px] font-bold text-text">{BILLING_KIND_QUESTION}</b>
        {BILLING_PLAN_KINDS.map((kind) => {
          const on = kind === plan.kind
          return (
            <div key={kind} data-kind-card={kind}
              className={cn('overflow-hidden rounded-[12px] border',
                on ? 'border-accent-line bg-accent-soft' : 'border-line bg-surface')}>
              <button type="button" disabled={busy} onClick={() => pick(kind)} aria-pressed={on}
                className="flex w-full items-center gap-3 px-3.5 py-3 text-left">
                <KindTile kind={kind} on={on} size={38} />
                <span className="min-w-0 flex-1">
                  <b className={cn('block text-[14.5px] font-bold', on ? 'text-accent-dark' : 'text-text')}>
                    {BILLING_KIND_CARDS[kind].title}
                  </b>
                  <span className={cn('block text-[13px]', on ? 'text-accent-dark' : 'text-text-muted')}>
                    {BILLING_KIND_CARDS[kind].sentence}
                  </span>
                </span>
                <span aria-hidden className={cn('flex h-[22px] w-[22px] shrink-0 items-center justify-center',
                  'rounded-full border-2', on ? 'border-accent' : 'border-line-strong')}>
                  {on && <span className="h-2.5 w-2.5 rounded-full bg-accent" />}
                </span>
              </button>
              {on && <div className="border-t border-accent-line bg-surface px-3.5 py-3">{rows}</div>}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 border-t border-line-soft bg-surface-alt px-[18px] pb-4 pt-3.5"
      data-kind-panel="">
      <div className="flex items-baseline gap-2 pb-0.5">
        <b className="shrink-0 text-[14px] font-bold text-text">{BILLING_KIND_QUESTION}</b>
        <span className="text-[12px] leading-[1.35] text-text-muted">{BILLING_KIND_QUESTION_SUB}</span>
      </div>
      <div className={cn('grid gap-2.5', layout === 'portrait' ? 'grid-cols-3' : 'grid-cols-5')}>
        {BILLING_PLAN_KINDS.map((kind) => {
          const on = kind === plan.kind
          return (
            <button key={kind} type="button" data-kind-card={kind} aria-pressed={on} disabled={busy}
              onClick={() => pick(kind)}
              className={cn('relative flex min-w-0 flex-col gap-[7px] rounded-[11px] border px-3 pb-3 pt-[11px]',
                'text-left',
                on ? 'border-accent-line bg-accent-soft ring-2 ring-accent-ring'
                  : 'border-line bg-surface hover:border-line-strong')}>
              {on && (
                <span aria-hidden className="absolute right-[9px] top-[9px] inline-flex h-4 w-4 items-center
                                             justify-center rounded-full bg-accent text-surface">
                  <IconCheck size={10} />
                </span>
              )}
              <KindTile kind={kind} on={on} size={30} />
              <span className="flex flex-col gap-0.5">
                <b className={cn('text-[13px] font-bold leading-[1.25]', on ? 'text-accent-dark' : 'text-text')}>
                  {BILLING_KIND_CARDS[kind].title}
                </b>
                <span className={cn('text-[11.5px] leading-[1.4]', on ? 'text-accent-dark' : 'text-text-muted')}>
                  {BILLING_KIND_CARDS[kind].sentence}
                </span>
              </span>
            </button>
          )
        })}
      </div>
      <div className="h-px bg-line-soft" />
      {rows}
    </div>
  )
}

function KindTile({ kind, on, size }: { kind: BillingPlanKind; on: boolean; size: number }) {
  return (
    <span aria-hidden className={cn('inline-flex shrink-0 items-center justify-center rounded-[9px] border',
      on ? 'border-accent-line bg-surface text-accent-dark' : 'border-line-soft bg-surface-alt text-text-muted')}
      style={{ width: size, height: size }}>
      <KindGlyph kind={kind} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// The rows of each kind (§8.2)
// ---------------------------------------------------------------------------

function KindRows(props: KindPanelProps) {
  const { plan } = props
  switch (plan.kind) {
    case 'completion': return <CompletionRows {...props} />
    case 'period': return <PeriodRows {...props} />
    case 'milestones': return <Stages {...props} />
    case 'deposit_balance': return <DepositRows {...props} />
    case 'time_materials': return <TimeMaterialsRows {...props} />
  }
  return null
}

/** A `MenuSelect` at a board width; on a phone it fills the row and opens as a sheet. */
function Pick<T extends string>({
  label, value, width, phone, options, onChange, disabled, title,
}: {
  label: string
  value: T
  width: number
  phone: boolean
  options: { value: T; label: string; help?: string; triggerLabel?: string }[]
  onChange: (value: T) => void
  disabled?: boolean
  title?: string
}) {
  return (
    <span className={phone ? 'min-w-0 flex-1' : 'shrink-0'} style={phone ? undefined : { width }}>
      <MenuSelect
        size="field"
        label={label}
        value={value}
        disabled={disabled}
        noSearch
        minWidth={width}
        sheet={{ title: title ?? label }}
        options={options}
        onChange={(next) => { if (next !== null && next !== value) onChange(next as T) }}
      />
    </span>
  )
}

function CompletionRows({ plan, layout, busy, onPatch }: KindPanelProps) {
  const phone = layout === 'phone'
  const words = BILLING_ROWS.completion
  return (
    <Row stack={phone} label={BILLING_ROWS.invoice}
      hint={plan.completionMode === 'last' ? words.lastHint : words.eachHint}>
      <Pick<CompletionMode> label={BILLING_ROWS.invoice} width={290} phone={phone} disabled={busy}
        value={plan.completionMode}
        options={[{ value: 'each', label: words.each }, { value: 'last', label: words.last }]}
        onChange={(completionMode) => void onPatch({ completionMode })} />
    </Row>
  )
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** `Every 1 month on the 1st` — the period's shape, shared with T&M's every-period. */
function EveryRow({ plan, layout, busy, onPatch }: KindPanelProps) {
  const phone = layout === 'phone'
  const every = (
    <>
      <Pick label={BILLING_ROWS.every} width={56} phone={phone} disabled={busy}
        value={String(plan.periodEvery)}
        options={Array.from({ length: 12 }, (_, index) => ({ value: String(index + 1), label: String(index + 1) }))}
        onChange={(next) => void onPatch({ periodEvery: Number(next) })} />
      <Pick<PeriodUnit> label="Period length" width={96} phone={phone} disabled={busy}
        value={plan.periodUnit}
        options={(['day', 'week', 'month'] as const).map((unit) => ({ value: unit, label: BILLING_ROWS.units[unit] }))}
        onChange={(periodUnit) => void onPatch({ periodUnit })} />
    </>
  )

  const day = plan.periodUnit === 'month'
    ? (
      <>
        {!phone && <Said tone="muted">{BILLING_ROWS.onThe}</Said>}
        {phone
          ? (
            <Pick label="On the" width={62} phone disabled={busy}
              value={String(plan.dayOfMonth ?? DEFAULT_BILLING_DAY.month)}
              options={Array.from({ length: 28 }, (_, index) => ({
                value: String(index + 1), label: BILLING_ROWS.onTheDay(index + 1),
              }))}
              onChange={(next) => void onPatch({ dayOfMonth: Number(next) })} />
          )
          : (
            <DayBox label={BILLING_ROWS.onThe} day={plan.dayOfMonth ?? DEFAULT_BILLING_DAY.month} disabled={busy}
              onCommit={(dayOfMonth) => void onPatch({ dayOfMonth })} />
          )}
      </>
    )
    : plan.periodUnit === 'week'
      ? (
        <>
          {!phone && <Said tone="muted">{BILLING_ROWS.on}</Said>}
          <Pick label={phone ? 'On' : BILLING_ROWS.on} width={112} phone={phone} disabled={busy}
            value={String(plan.dayOfWeek ?? DEFAULT_BILLING_DAY.week)}
            options={WEEKDAYS.map((label, value) => ({ value: String(value), label }))}
            onChange={(next) => void onPatch({ dayOfWeek: Number(next) })} />
        </>
      )
      : null

  if (phone) {
    return (
      <>
        <Row stack label={BILLING_ROWS.every}>{every}</Row>
        {day && <Row stack label={plan.periodUnit === 'month' ? 'On the' : 'On'}>{day}</Row>}
      </>
    )
  }
  return <Row label={BILLING_ROWS.every}>{every}{day}</Row>
}

function PeriodRows(props: KindPanelProps) {
  const { plan, layout, busy, onPatch } = props
  const phone = layout === 'phone'
  const [months, setMonths] = useState(false)
  // A package plan has no invoices to count yet, so an unset count is an empty box, not a nought.
  const count = plan.periodCount ?? (props.dateless ? Number.NaN : plan.invoices.length)
  const suffix = plan.partPeriod === 'prorate' ? BILLING_ROWS.theRental : BILLING_ROWS.visitsRun
  const window = forWindow(plan.window)

  return (
    <>
      <EveryRow {...props} />
      <Row stack={phone} label={BILLING_ROWS.for}>
        <QtyInput label={`${BILLING_ROWS.for} ${BILLING_ROWS.payments}`} width={phone ? 'w-full' : 'w-[52px]'}
          step={1} value={count} unit={phone ? BILLING_ROWS.payments : undefined}
          onCommit={(periodCount) => settled(onPatch({ periodCount: periodCount > 0 ? periodCount : null }))} />
        {!phone && <Said tone="muted">{BILLING_ROWS.payments}</Said>}
        {window && <Said>{window}, {suffix}</Said>}
        {!props.dateless && (
          <LinkButton onClick={() => setMonths((was) => !was)}>{BILLING_ROWS.changeMonths}</LinkButton>
        )}
      </Row>
      {months && (
        <BilledMonths plan={plan} phone={phone} busy={busy} onPatch={onPatch} onDone={() => setMonths(false)} />
      )}
      <Row stack={phone} label={BILLING_ROWS.billed}
        hint={plan.periodBasis === 'actual' ? BILLING_ROWS.arrearsHint : BILLING_ROWS.advanceHint}>
        <span className={phone ? 'w-full' : 'w-[260px]'}>
          <Segmented<PeriodBasis> size="field" full={phone} label={BILLING_ROWS.billed}
            value={plan.periodBasis}
            onChange={(periodBasis) => { if (periodBasis !== plan.periodBasis) void onPatch({ periodBasis }) }}
            options={[
              { value: 'even', label: BILLING_ROWS.inAdvance },
              { value: 'actual', label: BILLING_ROWS.inArrears },
            ]} />
        </span>
      </Row>
      {plan.periodUnit === 'month' && (
        <Row stack={phone} label={BILLING_ROWS.partPeriods}
          hint={plan.partPeriod === 'prorate' ? prorateHint(plan.window) : BILLING_ROWS.fullHint}>
          <Pick<PartPeriod> label={BILLING_ROWS.partPeriods} width={160} phone={phone} disabled={busy}
            value={plan.partPeriod}
            options={[
              { value: 'full', label: BILLING_ROWS.fullPeriod },
              { value: 'prorate', label: BILLING_ROWS.prorate },
            ]}
            onChange={(partPeriod) => void onPatch({ partPeriod })} />
        </Row>
      )}
    </>
  )
}

/**
 * Behind **Change months**: the billed window, which may differ from the
 * visits' (a season of work billed over twelve months). The board draws the
 * link and not what it opens, so this is the window's two dates and the way
 * back to following the visits — the controls the old tab had.
 *
 * One row under the For row's words, in the same control column (Aaron,
 * review item 5.2): the form kit's `DateInput`, 36 tall like the `1 ▾`
 * `month ▾` beside it. Each sits in a `flex` box of its own width and fills
 * it — the date field is `inline-flex` with no width of its own, so in a
 * plain 150px span it grew to its input's natural size, ran over the "to"
 * and the box after it and half-covered *Follow the visits*. On a phone the
 * two share the row, and anything that does not fit wraps whole.
 */
function BilledMonths({
  plan, phone, busy, onPatch, onDone,
}: { plan: QuoteBillingPlanView; phone: boolean; busy: boolean; onPatch: Patch; onDone: () => void }) {
  const box = phone ? 'flex min-w-[132px] flex-1' : 'flex w-[150px] shrink-0'
  const pair = (
    <>
      <span className={box}>
        <DateInput label="Billed from" className="w-full" disabled={busy}
          value={plan.billFrom ?? plan.window?.from ?? ''}
          onChange={(next) => void onPatch({ billFrom: next || null })} />
      </span>
      <Said tone="muted">to</Said>
      <span className={box}>
        <DateInput label="Billed to" className="w-full" disabled={busy}
          value={plan.billTo ?? plan.window?.to ?? ''}
          onChange={(next) => void onPatch({ billTo: next || null })} />
      </span>
      <LinkButton disabled={busy} onClick={() => { onDone(); void onPatch({ billFrom: null, billTo: null }) }}>
        Follow the visits
      </LinkButton>
    </>
  )
  if (phone) {
    return <div className="flex min-w-0 flex-wrap items-center gap-2" data-billed-months="">{pair}</div>
  }
  return <Row label=""><span className="contents" data-billed-months="">{pair}</span></Row>
}

function DepositRows({ plan, layout, busy, onPatch }: KindPanelProps) {
  const phone = layout === 'phone'
  const flat = plan.depositFlat !== null
  const value = flat ? plan.depositFlat! : plan.depositPct ?? 50
  const deposit = plan.invoices.find((row) => row.role === 'deposit')
  return (
    <>
      <Row stack={phone} label={BILLING_ROWS.deposit} hint={BILLING_ROWS.depositHint}>
        <QtyInput label={BILLING_ROWS.deposit} width={phone ? 'w-full' : 'w-[72px]'}
          className={phone ? 'min-w-0 flex-1' : undefined} value={value}
          onCommit={(next) => settled(onPatch(flat ? { depositFlat: next } : { depositPct: Math.min(100, next) }))} />
        <UnitToggle label={`${BILLING_ROWS.deposit} as`} width={84} unit={flat ? 'flat' : 'pct'}
          onChange={(unit) => {
            if (unit === (flat ? 'flat' : 'pct')) return
            // The number carries across; only the unit flips (§8.2).
            void onPatch(unit === 'flat' ? { depositFlat: value } : { depositPct: Math.min(100, value) })
          }} />
        <Said tone="muted">
          {flat ? BILLING_ROWS.flatOnApproval : BILLING_ROWS.pctOnApproval(usd(deposit?.amount ?? 0))}
        </Said>
      </Row>
      <Row stack={phone} label={BILLING_ROWS.theRest}
        hint={plan.restMode === 'spread' ? BILLING_ROWS.rest.spreadHint : BILLING_ROWS.rest.lastHint}>
        <Pick<RestMode> label={BILLING_ROWS.theRest} width={330} phone={phone} disabled={busy}
          value={plan.restMode}
          options={[
            { value: 'last', label: BILLING_ROWS.rest.last },
            { value: 'spread', label: BILLING_ROWS.rest.spread },
          ]}
          onChange={(restMode) => void onPatch({ restMode })} />
      </Row>
    </>
  )
}

function TimeMaterialsRows(props: KindPanelProps) {
  const { plan, layout, busy, onPatch } = props
  const phone = layout === 'phone'
  const ceiling: ReactNode = plan.capAmount !== null
    ? (
      <Row stack={phone} label={BILLING_ROWS.ceiling} hint={BILLING_ROWS.ceilingHint}>
        <Said tone="muted">{BILLING_ROWS.notToExceed}</Said>
        <QtyInput label={BILLING_ROWS.notToExceed} width={phone ? 'w-full' : 'w-[104px]'}
          className={phone ? 'min-w-0 flex-1' : undefined} value={plan.capAmount}
          onCommit={(capAmount) => settled(onPatch({ capAmount: capAmount > 0 ? capAmount : null }))} />
        <LinkButton tone="faint" disabled={busy} onClick={() => void onPatch({ capAmount: null })}>
          {BILLING_ROWS.remove}
        </LinkButton>
      </Row>
    )
    : (
      <Row stack={phone} label={BILLING_ROWS.ceiling} hint={BILLING_ROWS.addCeilingHint}>
        {/* Starts at the estimate, the one number somebody would type first. */}
        <LinkButton disabled={busy} onClick={() => void onPatch({ capAmount: plan.total > 0 ? plan.total : 1 })}>
          {BILLING_ROWS.addCeiling}
        </LinkButton>
      </Row>
    )
  return (
    <>
      <Row stack={phone} label={BILLING_ROWS.invoice} hint={BILLING_ROWS.tmHint}>
        {!phone && <Said tone="muted">{BILLING_ROWS.tmUsed}</Said>}
        <Pick<TimeMaterialsTrigger> label={BILLING_ROWS.invoice} width={232} phone={phone} disabled={busy}
          value={plan.tmTrigger}
          options={(['job_completion', 'visit_completion', 'period'] as const)
            .map((value) => ({ value, label: BILLING_ROWS.tm[value] }))}
          onChange={(tmTrigger) => void onPatch({ tmTrigger })} />
      </Row>
      {plan.tmTrigger === 'period' && <EveryRow {...props} />}
      {ceiling}
    </>
  )
}
